import { SCORE_OPERATORS } from "./constant";

export type View = "list" | "builder";
export type Tab = "Triggers" | "Actions";
export type TrackLeadsFrom = "All Broadcasts" | "Specific Broadcast" | "Audience Segment";

// ─── Keyword entry (matches backend shape) ────────────────────────────────────
export interface KeywordEntry {
  id: string;           // e.g. "kw_1"
  text: string;
  match_similar: boolean;
}

export type JourneyPresetState = {
  journeyPreset?: {
    title?: string;
    triggerType?: "On Message" | "Run at Specific Time" | "New Lead" | "On Event" | "RCS Trigger Node";
    presetActions?: string[];
  };
};

// ─── Bot Journey ──────────────────────────────────────────────────────────────
export interface BotJourney {
  channel: "whatsapp" | "sms";
  account: string;
  sessions: number;
  created_at: string;
  dateCreated: string;
  updated_at: string;
  id: number;
  tenant: string;
  name: string;
  status: "draft" | "active" | "inactive" | "archived" | "published";
  active_version: number | null;
  versions?: BotJourneyVersion[];
}

export interface BotJourneyVersion {
  id: number;
  version: number;
  definition: BotJourneyDefinition;
  status: "draft" | "active" | "inactive" | "archived";
  created_at: string;
}

// New flat definition shape from the backend
export interface BotJourneyDefinition {
  trigger: string;                     // e.g. "on_message"
  trigger_config: BotJourneyTriggerConfig;
  steps: BotJourneyStep[];
}

export interface BotJourneyTriggerConfig {
  keywords?: KeywordEntry[];
  restart_journey?: boolean;
  fixed_time?: string;
  trigger_interval?: string;
  timezone?: string;
  recipients?: string[];
}

export interface BotJourneyStepConfig {
  // send_message
  body?: string;
  media_url?: string | null;
  media_type?: string | null;
  // send_template
  template_name?: string;
  language?: string;
  components?: unknown[];
  // add_tags
  tags?: string[];
  // assign_agent
  agent_id?: string;
  // condition
  conditions?: {
    variable: string;
    operator: string;
    value: string;
  }[];
  logic?: "AND" | "OR";
  true_next?: string;
  false_next?: string;
  // call_api
  url?: string;
  method?: "GET" | "POST" | "PUT" | "DELETE";
  headers?: Record<string, string>;
  // allow extra
  [key: string]: unknown;
}

export interface BotJourneyStep {
  id: string;
  type:
  | "assign_agent"
  | "add_tags"
  | "send_message"
  | "send_template"
  | "close_conversation"
  | "call_api"
  | "condition"
  | string;
  config: BotJourneyStepConfig;
}

// ─── Canvas node types ────────────────────────────────────────────────────────

export interface TriggerNode {
  type: string;
  // On Message fields
  keywords?: KeywordEntry[];
  restart_journey?: boolean;
  fixedTime?: string;
  triggerInterval?: string;
  timezone?: string;
  recipients?: string;
  // Legacy CTWA fields
  waba?: string;
  trackLeadsFrom?: TrackLeadsFrom;
  broadcast?: string;
  segment?: string;
  [key: string]: unknown;
}

export interface ActionNode {
  id: string;
  type:
  | "Assign Agent"
  | "Add or Remove Tags"
  | "Condition"
  | "Send Message"
  | "Add to Broadcast"
  | "Store Data"
  | "Send Internal Notification"
  | "Create Support Ticket"
  | "Close Conversation"
  | "Call API"
  | "Format Date"
  | "Format Mobile Number"
  | "Send Interactive Message"
  | "Set Delay Time"
  | "Run at Specific Time"
  | "Wait Until"
  | "Fetch Current Date"
  | "Fetch Audience"
  | "Mark as Sent"
  | "Set Customer Attribute"
  | "Send Message Template"
  | "WhatsApp Message"
  | "Send Catalogue"
  | "Send Birthday Message"
  | "Send Email"
  | "Wait Till"
  // RCS-only action types (used when the trigger is "RCS Trigger Node")
  | "Send RCS Template"
  | "Send Menu"
  | "Send Carousel"
  | "Send Text"
  | "Wait for Reply"
  | "Set Variable"
  | "Escalate to Human"
  | "Confirm Order"
  | "Branch"
  config: Record<string, any>;
  x: number;
  y: number;
}


// ─── RCS backend shape (services/rcs_bot_service) ────────────────────────────
// The RCS trigger's Save Draft/Publish calls go to a separate backend
// (POST /rcs/bots, PUT /rcs/bots/{id}/draft, POST /rcs/bots/{id}/publish —
// see useRcsBotReq) whose stored shape is a node/edge graph, not a step
// array. actionNodesToRcsFlow/rcsFlowToActionNodes in constant.ts translate
// between that shape and this app's linear TriggerNode/ActionNode[] model.
export interface RcsChip {
  text: string;
  postbackData: string;
}

export interface RcsCarouselCard {
  title: string;
  description?: string;
  imageUrl?: string;
  suggestions: { text: string; postbackData: string }[];
}

export type RcsNodeType =
  | "trigger"
  | "send_menu"
  | "send_carousel"
  | "send_text"
  | "send_rcs_template"
  | "send_email"
  | "call_api"
  | "wait_for_reply"
  | "set_variable"
  | "branch"
  | "escalate"
  | "confirm_order";

export interface RcsFlowNode {
  id: string;
  type: RcsNodeType;
  label: string;
  position: { x: number; y: number };
  config: Record<string, any>;
}

export interface RcsFlowEdge {
  id: string;
  source: string;
  target: string;
  condition: "postback" | "text_contains" | "default" | "always";
  value?: string;
  label?: string;
}

export interface RcsFlowDefinition {
  startNodeId: string;
  nodes: RcsFlowNode[];
  edges: RcsFlowEdge[];
  meta?: Record<string, unknown>;
}

export const makeKwId = (idx: number) => `kw_${idx + 1}`;

export const DEFAULT_TRIGGER_INTERVAL = "Days";

export const makeScheduledConfig = () => ({
  fixedTime: "",
  triggerInterval: DEFAULT_TRIGGER_INTERVAL,
  recipients: "",
});

export const PRESET_WARM_ID = "preset_3_whatsapp_message";
export const PRESET_COLD_ID = "preset_4_whatsapp_message";

export const WAIT_TIMEOUT_UNIT_MULTIPLIERS: Record<string, number> = {
  minutes: 60,
  hours: 3600,
  days: 86400,
};


export const normalizeConditionValue = (variable: string, value: unknown) => {
  if (variable !== "score") return value ?? "";

  const numericValue = Number(value);
  return Number.isNaN(numericValue) ? "" : numericValue;
};

export const normalizeConditionOperator = (variable: string, operator?: string) => {
  if (variable === "score" && (!operator || !SCORE_OPERATORS.has(operator))) {
    return "greater_than";
  }

  return operator || "equals";
};

export const buildSingleCondition = (condition: any) => {
  const variable = condition?.variable ?? condition?.customerAttribute ?? "user_response";
  const operator = normalizeConditionOperator(variable, condition?.operator ?? condition?.condition);

  return {
    variable,
    operator,
    value: normalizeConditionValue(variable, condition?.value),
  };
};